// ErrorApi.js
// This is the API module for calling error logging to the server.

import axios from "axios";
import { getCsrfToken, setupCsrfInterceptor } from "../auth/csrfUtils";

// Create axios instance with default config
const api = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  // Session cookies are required for this API
  withCredentials: true,
});

// Setup CSRF protection for this API instance
setupCsrfInterceptor(api);

// Error API endpoints
const baseErrorUrl = "/errors";

// Client-side cap so an error loop cannot flood the server
const MAX_LOGS_PER_MINUTE = 10;
const RATE_LIMIT_PAUSE_MS = 10 * 60 * 1000;
let recentSends = [];
let pausedUntil = 0;

/**
 * Log an error to the backend
 * @param {Object} errorData - Error information to log
 * @returns {Promise<Object|null>} Response data or null if failed or throttled
 */
export const logError = async (errorData) => {
  const now = Date.now();
  if (now < pausedUntil) return null;
  recentSends = recentSends.filter((sentAt) => now - sentAt < 60 * 1000);
  if (recentSends.length >= MAX_LOGS_PER_MINUTE) return null;
  recentSends.push(now);

  try {
    const response = await api.post(`${baseErrorUrl}/log`, errorData, {
      skipCsrf: true, // Don't require CSRF for error logging (no auth required)
    });
    return response.data;
  } catch (error) {
    // Server is throttling this client; stop sending until its window resets
    if (error.response?.status === 429) {
      pausedUntil = Date.now() + RATE_LIMIT_PAUSE_MS;
    }
    // Fail silently - don't break app if error logging fails
    console.error("Failed to log error to server:", error);
    return null;
  }
};
